/** "ONE for Outlook": download the add-in (public/outlook/manifest.xml) and how to install it. Shown on Apps, Mail and Settings. */
import { Copy, Download } from "lucide-react";
import { useState } from "react";
import { Card } from "./ui";

export const ADDIN_URL = "https://one.infinite-fusion.com/outlook/manifest.xml";

export function OutlookAddinCard() {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(ADDIN_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Card title="ONE for Outlook — download the add-in">
      <div className="flex flex-wrap items-start gap-4">
        <img src="/outlook/icon-80.png" alt="" width={48} height={48} className="rounded-lg" />
        <div className="min-w-0 flex-1">
          <p className="text-sm">Adds an <strong>Add to ONE</strong> button to Outlook: open any email, click it, and it becomes a task in your own ONE tasks, linked back to the email. For Outlook on the web, the new Outlook, and Outlook for Windows and Mac.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a className="btn btn-primary" href="/outlook/manifest.xml?download=1" download="ONE-for-Outlook.xml"><Download size={15} /> Download ONE for Outlook</a>
            <button className="btn" onClick={copy}><Copy size={15} /> {copied ? "Link copied" : "Copy the install link"}</button>
          </div>
        </div>
      </div>
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer font-medium">How to install the downloaded file (2 minutes)</summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>Click <strong>Download ONE for Outlook</strong> above. The file <code className="rounded bg-[var(--panel-2)] px-1">ONE-for-Outlook.xml</code> is saved to your Downloads.</li>
          <li>In Outlook, open <strong>Get Add-ins</strong>:
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li><strong>Outlook on the web / new Outlook:</strong> open any email → <strong>Apps</strong> (or the <strong>…</strong> menu) → <strong>Get Add-ins</strong>.</li>
              <li><strong>Outlook for Windows (classic):</strong> <strong>Home</strong> tab → <strong>Get Add-ins</strong> (or <strong>All Apps</strong> → <strong>Add Apps</strong>).</li>
              <li><strong>Outlook for Mac:</strong> open an email → <strong>…</strong> → <strong>Get Add-ins</strong>.</li>
            </ul>
          </li>
          <li>Choose <strong>My add-ins</strong> → <strong>Custom add-ins</strong> → <strong>Add a custom add-in</strong> → <strong>Add from file…</strong>, pick <code className="rounded bg-[var(--panel-2)] px-1">ONE-for-Outlook.xml</code> and confirm with <strong>Install</strong>.</li>
          <li>Open an email and click <strong>Add to ONE</strong>. The first time, sign in to ONE in the small window that opens.</li>
        </ol>
        <p className="muted mt-2">Prefer not to download? Use <strong>Add from URL</strong> in step 3 with <code className="rounded bg-[var(--panel-2)] px-1">{ADDIN_URL}</code>. It installs once for your mailbox and then shows in Outlook on all your devices. If your Outlook doesn't offer custom add-ins, ask an admin to install it for you.</p>
      </details>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer font-medium">For admins: install it for everyone</summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>Microsoft 365 admin center → <strong>Settings</strong> → <strong>Integrated apps</strong> → <strong>Upload custom apps</strong>.</li>
          <li>Choose <strong>Office Add-in</strong>, then upload the downloaded file or paste the install link.</li>
          <li>Pick who gets it (everyone, or specific people or groups) and deploy. It appears in their Outlook within a few hours.</li>
        </ol>
      </details>
    </Card>
  );
}
